import { useQuery, useQueryClient } from '@tanstack/react-query';
import api, { DocumentSignEnvelopeDetailResponse } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

interface UseEnvelopeReturn {
  envelope: DocumentSignEnvelopeDetailResponse | null;
  loading: boolean;
  /** A refresh is in flight — what is shown may be about to change. */
  fetching: boolean;
  error: string | null;
  refresh: () => void;
}

export function useEnvelope(envelopeId: string | null | undefined): UseEnvelopeReturn {
  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: queryKeys.documentSign.envelope(envelopeId ?? '__none__'),
    queryFn: () => api.getDocumentSignEnvelope(envelopeId as string),
    enabled: !!envelopeId,
  });

  return {
    envelope: data?.envelope ?? null,
    loading: isLoading,
    fetching: isFetching,
    error: error instanceof Error ? error.message : error ? 'Failed to load envelope' : null,
    refresh: () => {
      refetch();
    },
  };
}

/**
 * Read an envelope fresh from the server — for a decision that must not act on
 * a cached copy (one that may predate saves just made). Goes through the
 * envelope's own query, so the cache is refreshed with what was read.
 */
export function useFetchFreshEnvelope(): (
  envelopeId: string,
) => Promise<DocumentSignEnvelopeDetailResponse> {
  const queryClient = useQueryClient();
  return async (envelopeId: string) => {
    const data = await queryClient.fetchQuery({
      queryKey: queryKeys.documentSign.envelope(envelopeId),
      queryFn: () => api.getDocumentSignEnvelope(envelopeId),
      staleTime: 0,
    });
    return data.envelope;
  };
}
